import React from 'react'

import {
  AlignItems,
  Body3,
  BorderStyle,
  Box,
  Color,
  Display,
  FlexDirection,
  FontFamily,
  JustifyContent,
  Text,
  withCitrusTheme,
} from '@gousto-internal/citrus-react'
import GoustoImage from 'Image'
import type Immutable from 'immutable'

import { formatUseByDateRange } from '@library/menu-service'
import type { Recipe } from '@library/menu-service'

const SummaryImage = withCitrusTheme(GoustoImage, () => ({
  borderRadius: '8px',
  height: '62px',
  objectFit: 'cover',
  width: '62px',
}))

type RecipeItemProps = {
  boxPortions: number
  deliveryDate: string
  media: Immutable.List<Immutable.Map<string, string | number>>
  numPortions: number
  shelfLife?: Recipe['shelfLife'] | null
  title: string
}

export const RecipeItem = ({
  boxPortions,
  deliveryDate,
  media,
  numPortions,
  shelfLife = null,
  title,
}: RecipeItemProps) => {
  const useByText = formatUseByDateRange(deliveryDate, shelfLife, { includeWeekday: true })
  const showPortionBadge = numPortions !== boxPortions

  return (
    <Box
      display={Display.Flex}
      alignItems={AlignItems.Center}
      gap={2}
      paddingV={2}
      borderBottomWidth={0.5}
      borderColor={Color.ColdGrey_100}
      borderStyle={BorderStyle.Solid}
      data-testid="order-summary-recipe-item"
    >
      <Box
        width="62px"
        height="62px"
        flexShrink={0}
        style={{ position: 'relative', overflow: 'hidden', borderRadius: '8px' }}
      >
        {media.size > 0 ? (
          <SummaryImage media={media} title={title} contain={false} />
        ) : (
          <Box width="62px" height="62px" bg={Color.NeutralGrey_200} />
        )}
        {showPortionBadge && (
          <Box
            style={{
              position: 'absolute',
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(255, 255, 255, 0.6)',
              boxSizing: 'border-box',
              height: '21px',
            }}
          >
            <Text
              color={Color.ColdGrey_800}
              fontFamily={FontFamily.Bold}
              style={{
                whiteSpace: 'nowrap',
                textAlign: 'center',
                fontSize: '10px',
                lineHeight: '21px',
              }}
            >
              {numPortions} portion{numPortions === 1 ? '' : 's'}
            </Text>
          </Box>
        )}
      </Box>
      <Box
        display={Display.Flex}
        flexDirection={FlexDirection.Column}
        justifyContent={JustifyContent.SpaceBetween}
        flexGrow={1}
        minWidth="0"
        height="62px"
      >
        <Text
          size={3}
          color={Color.ColdGrey_800}
          fontFamily={FontFamily.Bold}
          style={{
            overflow: 'hidden',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
          }}
        >
          {title}
        </Text>
        {useByText && <Body3>{useByText}</Body3>}
      </Box>
    </Box>
  )
}
